/**
 * This file contains helper functions for Campaign Managment.
 *
 * The entire purpose of Campaign Management is to determine:
 * 1. Where marketing dollars are going
 * 2. What the payback is from that money
 *
 * That's pretty much it.  In order to determine this, we need to look at ALL of the money going toward the campaign,
 * not just dollars.  Time is money for an attorney, so time spent working on a campaign, whether that is setting it
 * up, creating content, monitoring results, or tweaking the campaign, that is time spent and time = money.
 *
 * Specifically, time * billableRate = money.
 *
 * Return on a campaign is measured in terms of billed and collected funds from a client that was referred to us
 * through a campaign.  Note that a lead may use multiple means to find us, and we need to split the returns over
 * those campaigns.  So if a lead got to us through a Google search and really like our website, then we should
 * split their billed and paid money between those campaigns!  This is where it becomes subjective, though.  If the
 * client followed the Google Ad to our website, just so that they could click on the Call Us button, then did the
 * website really influence them?  Probably not.  In that case, is it worth mentioning the web site?  Totally subjective.
 *
 * Right now we are only looking at total costs in and total income received.  In the future we will look at rolling
 * averages to see if the influence of a campaign is increasing or decreasing over time.
 */

import util from "../../../lib/util";
import MathUtil from "../../../lib/mathUtils";
import store from "../../../store/store";
import FinancialHelper, {BilledAndPaidInvoice} from '../../../Financial/FinancialHelper';
import LeadHelper from '../../Sales/LeadHelper';
import Icons from '../../../ui/IconButton/Icons';
import {campaignTypeIcons} from './Campaigns';
import ChartProps from '../../../Charts/ChartProps';
import * as DateUtil from '../../../lib/dateutils';
import * as dfn from 'date-fns';
import {getUpdatedCost} from "../../../TaskManagement/TaskHelper";
import Campaign from "../../../entities/campaign";
import Task from "../../../entities/task";
import Matter from "../../../entities/matter";
import {uuid} from "../../../@types/entity";
import Lead from "../../../entities/lead";
import ArrayMap from "../../../lib/ArrayMap";

export type MatterInfo = {
    matter: Matter;
    bapis: BilledAndPaidInvoice[];
    billed: number;
    collected: number;
}

export type CampaignBilledAndCollected = {
    billed: number,
    collected: number,
    matterInfo: MatterInfo[];
};

/**
 * Get (total) hard costs for a campaign, as a number used for calculations.
 *
 * @param {Campaign} campaign
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @returns {number}
 */
export const getHardCosts = (campaign: Campaign, startDate?: Date, endDate?: Date): number => {
    if (!startDate) startDate = new Date(0);
    if (!endDate) endDate = new Date();

    return util.toTwoDecimals(campaign.hardCosts
        .filter(c => DateUtil.isBetween(DateUtil.parseDate(c.incurredOn), startDate, endDate))
        .reduce((total, current) => total + current.cost, 0));
};

/**
 * Return the campaign related to this campaignId.
 *
 * @param {uuid} campaignId
 * @returns {Campaign}
 */
export function getCampaignFromId(campaignId: uuid): Campaign {
    return store.getState().campaigns.get(campaignId);
}

/**
 * Get labor costs in the given date range.
 *
 * @param {Campaign} campaign
 * @param {Task[]} tasks
 * @param {boolean} formatted
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @return {number}
 */
export const getLaborCosts = (campaign: Campaign, tasks: ArrayMap<Task> | Task[], formatted?: boolean, startDate?: Date, endDate?: Date): string | number => {
    if (!startDate) startDate = new Date(0);
    if (!endDate) endDate = new Date();

    if (!tasks) tasks = store.getState().tasks.getMappingFor('campaignId', campaign.id);

    // Have to get all the tasks, do the divvying up, etc.
    const filteredTasks = tasks
        .filter(t => {
            // Check the dates
            if (!DateUtil.isBetween(t.completionDate, startDate, endDate)) return false;

            // See if this task is related to this campaign
            return t.tieIns.some(ti => ti.module === 'Marketing' && ti.entity === 'Campaign' && ti.id === campaign.id);
        });

    const total = filteredTasks
        .map(task => {
            // If there is more than one campaign attached to this task, figure out the split (how many related campaigns)
            const split = task.tieIns.filter(ti => ti.module === 'Marketing' && ti.entity === 'Campaign').length;

            return getUpdatedCost(task) / (split || 1);
        })
        .reduce((total, current) => total + current, 0);

    return formatted ? util.numberWithCommas(total) : util.toTwoDecimals(total);
}

/**
 * Get recurring costs for a campaign.
 *
 * @param {Campaign} campaign
 * @param {boolean} formatted
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @return {number | string}
 */
export const getRecurringCosts = (campaign: Campaign, formatted?: boolean, startDate?: Date, endDate?: Date): number | string => {
    if (!startDate) startDate = new Date(0);
    if (!endDate) endDate = new Date(Number.MAX_SAFE_INTEGER);

    const total = campaign.recurringCosts
        .filter(c => {
            const cStart = new Date(c.startDate);
            const cEnd = new Date(c.endDate);
            return cStart <= endDate && cEnd >= startDate;
        })
        .reduce((total, current) => {
            const cStart = new Date(current.startDate);
            const cEnd = new Date(current.endDate);
            const totalDays = Math.max(1, (cEnd.getTime() - cStart.getTime()) / 86400000);
            const overlapStart = new Date(Math.max(cStart.getTime(), startDate.getTime()));
            const overlapEnd = new Date(Math.min(cEnd.getTime(), endDate.getTime()));
            const overlapDays = Math.max(0, (overlapEnd.getTime() - overlapStart.getTime()) / 86400000);
            return total + (current.cost * overlapDays / totalDays);
        }, 0);

    return formatted ? util.numberWithCommas(total) : util.toTwoDecimals(total);
};

/**
 * Get all costs, as a string
 *
 * @param {Campaign} campaign - the campaign to total
 * @param {Task[]} [tasks]
 * @param {boolean} [formatted] - format with $ and commas?
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @returns {number|string}
 */
export const getTotalCost = (campaign: Campaign, tasks?: Task[], formatted?: boolean, startDate?: Date, endDate?: Date): number | string => {
    if (!tasks) tasks = store.getState().tasks.getMappingFor('campaignId', campaign.id);

    const total = (getHardCosts(campaign, startDate, endDate) as number)
        + (getLaborCosts(campaign, tasks, false, startDate, endDate) as number)
        + (getRecurringCosts(campaign, false, startDate, endDate) as number);

    return formatted ? util.numberWithCommas(total) : util.toTwoDecimals(total);
};

export const getIcon = (campaign: Campaign) => {
    let icon = campaignTypeIcons.get(campaign.category);
    if (/facebook/i.test(campaign.name)) icon = Icons.FACEBOOK;
    else if (/google/i.test(campaign.name)) icon = Icons.GOOGLE;
    else if (/twitter/i.test(campaign.name)) icon = Icons.TWITTER;
    else if (/yelp/i.test(campaign.name)) icon = Icons.YELP;

    return icon;
};

/**
 * Get the components of the ROI for a campaign, given optional date ranges. Note that the returns are how much the
 * campaign INFLUENCED, not a direct return, as there can be more than 1 influencer for a lead.
 *
 * Dates are used to determine the spend (campaign costs) and returns (invoices) used in the calculation.
 *
 * When there are multiple campaigns and/or referrals for a matter, we need to proportion them out.
 *
 * So if we have a lead with two campaigns and a referral, e.g. website, Google and Bob, the credit will be allocated
 * as such:
 * - Bob get 50%
 * - Google gets 25%
 * - Website gets 25%
 *
 * Methodology:  Bob = 2, Google = 1, Website = 1: total 4, thus 100%/4 = 25% per unit.
 *
 * @param {Campaign} campaign
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @return {CampaignBilledAndCollected}
 */
export const getCampaignBilledAndCollected = (campaign: Campaign, startDate?: Date, endDate?: Date): CampaignBilledAndCollected => {
    if (!startDate) startDate = new Date(0);
    if (!endDate) endDate = new Date();

    // Keep track of the total influenced dollars, both billed and collected
    let billed = 0;
    let collected = 0;

    /**
     * Get all leads associated with a campaign
     */
    const leads = store.getState().leads.getMappingFor('campaignId', campaign.id);

    /**
     * Store matters for these leads.
     */
    const matterInfo: Array<MatterInfo> = [];

    /* For each lead, get the related campaigns and referrers */
    leads.forEach(lead => {
        /**
         * Get all matters associated with this lead
         */
        const matters = store.getState().matters.getMappingFor('leadId', lead.id);

        // For each matter, get the influenced amount.
        matters.forEach(matter => {
            /**
             * Invoices for this matter, if any.
             */
            let matterInvoices = FinancialHelper.getInvoices(matter.id, 'asc');

            // Get the first invoice for the matter by looking up all the invoices, taking the first one,
            // and getting the completion date of the first task (if any).
            const invoiceTasks = matterInvoices[0]?.taskIds
                .map(t => store.getState().tasks.get(t))
                .sort((a, b) => (a.completionDate?.getTime() ?? 0) - (b.completionDate?.getTime() ?? 0)) ?? [];

            const matterCreated = invoiceTasks.filter(util.existsFilter).length > 0 && invoiceTasks[0].completionDate
                                  ? invoiceTasks[0].completionDate.getTime() - 1
                                  : 0;

            // If there are no invoices and no startingBilled or startingCollected, just return.
            // Otherwise, calculate relevant bapis.
            if (matterCreated || matter.startingBilled || matter.startingCollected) {
                let _billed = 0;
                let _collected = 0;

                // Figure out if we need to include startingBilled and collected
                const isBetween = matterCreated > 0 ? dfn.isBefore(matterCreated, endDate)
                                                    : DateUtil.isBetween(matterCreated, startDate, endDate);
                if (isBetween) {
                    _billed = matter.startingBilled;
                    _collected = matter.startingCollected;
                }

                // For this specific matter, calculate the amounts billed and collected.
                const bapis = FinancialHelper.getBilledAndPaidForMatters([matter.id]);

                bapis.filter(bapi => DateUtil.isBetween(bapi.endDate, startDate, endDate))
                    .forEach((bapi) => {
                        _billed += bapi.billed;
                        _collected += bapi.collected;
                        billed += bapi.billed;
                        collected += bapi.collected;
                    });

                matterInfo.push({matter, bapis, billed: _billed, collected: _collected});
            }
        });
    });

    return {
        billed: util.toTwoDecimals(billed),
        collected: util.toTwoDecimals(collected),
        matterInfo
    };
};


/**
 * Get the components of the ROI.
 *
 * When there are multiple campaigns and/or referrals for a matter, we need to proportion them out.
 * For now we will assume that a referral is much more powerful than a campaign, and will garner [referralWeight] the credit.
 *
 * So if we have a lead with two campaigns and a referral, e.g. website, Google and Bob, the credit will be allocated
 * as such:
 * - Bob get 50%
 * - Google gets 25%
 * - Website gets 25%
 *
 * Methodology:  Bob = 2, Google = 1, Website = 1: total 4, thus 100%/4 = 25% per unit.
 *
 * @param {number} referralWeight
 * @return {{billed: number, collected: number}}
 */
export const getReferralRoi = (referralWeight: number = 1): CampaignBilledAndCollected => {
    // Keep track of the total influenced dollars, both billed and collected
    let billed = 0;
    let collected = 0;

    const matterInfo = [];

    /**
     * Get all leads associated with referrals
     */
    const leads = store.getState().leads.filter(lead => lead.referrers.length > 0);

    leads.forEach(lead => {
        const campaigns = lead.campaigns.length;
        const referrals = lead.referrers.length;
        const influence = (referrals * referralWeight) / (campaigns + (referrals * referralWeight));

        const matters = store.getState().matters.getMappingFor('leadId', lead.id);

        // For each matter, get the influenced amount based on the campaign's lead influence.
        matters.forEach(matter => {
            let thisBilled = matter.startingBilled;
            let thisCollected = matter.startingCollected;
            billed += thisBilled;
            collected += thisCollected;

            // For this specific matter, calculate the amounts billed and collected.
            const bapis = FinancialHelper.getBilledAndPaidForMatters([matter.id]);

            bapis.forEach((bapi) => {
                thisBilled += bapi.billed;
                thisCollected += bapi.collected;
                billed += bapi.billed;
                collected += bapi.collected;
            });

            matterInfo.push({matter, bapis, billed: thisBilled / influence, collected: thisCollected / influence});
        });
    });

    return {
        billed: util.toTwoDecimals(billed),
        collected: util.toTwoDecimals(collected),
        matterInfo
    };
};

/**
 * Get cases for a campaign
 *
 * @param {Campaign} campaign
 */
export const getCases = (campaign: Campaign) => {
    return store.getState().matters.getMappingsFor('leadId', getLeadIds(campaign)); // Matters with leads
};

/**
 * Get lead Ids for a campaign
 *
 * @param {Campaign} campaign
 */
export function getLeadIds(campaign: Campaign) {
    return getLeads(campaign).map(l => l.id);
}

/**
 * get Leads for a campaign
 * @param {Campaign} campaign
 */
export const getLeads = (campaign: Campaign) => {
    return util.distinctId<Lead>(store.getState().leads
        .filter(l => l?.campaigns.includes(campaign.id))
        .filter(util.existsFilter));
};

/**
 * Get the average cost per lead for this campaign.  This is the cost to get a person to contact the firm, so
 * any lead will do.  Take the total cost of the campaign, so far, and divide by the number of leads.
 *
 * If no campaign is passed we get the overall (average) cost per lead.
 *
 * @param {Campaign} [campaign]
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 * @returns {number}
 */
export const getCostPerLead = (campaign?: Campaign, startDate?: Date, endDate?: Date): number => {
    if (campaign) {
        const totalCost = getTotalCost(campaign, null, false, startDate, endDate) as number;

        const allLeads = store.getState().leads
            .getMappingFor('campaignId', campaign.id)
            .map(l => l.id)
            .filter(util.existsFilter)
            .filter(util.distinctFilter);

        if (allLeads.length === 0) return totalCost;

        return util.fixed(totalCost / (allLeads.length || 1), 2);
    }
    else {
        // Get the global average
        const totalLeads = store.getState().leads.filter(util.deletedFilter).length;

        const totalCost = MathUtil.sum(store.getState().campaigns
            .filter(util.deletedFilter)
            .map(c => getTotalCost(c) as number));

        if (totalCost === 0) return 0;
        else return util.fixed(totalCost / totalLeads, 2);
    }
};

/**
 * Get the conversion rate for leads from this campaign.  This is the number of leads with this campaign as a
 * contributing factor that lead to a won case (meaning a case has been created from the lead).
 *
 * Note that conversion rate is defined as
 * If no campaign is passed, we get global average conversion rate.
 *
 * @param {Campaign} [campaign]
 * @returns {number}
 */
export const getConversionRate = (campaign?: Campaign): number => {
    let convertedLeads: string | any[];
    let qualifiedLeads: string | any[];

    if (campaign) {
        convertedLeads = util.distinctId<Lead>(store.getState().matters
            .getIndex('hasLead')
            .map(m => LeadHelper.getLeadFromId(m.leadId)) // Lead objects
            .filter(util.existsFilter)
            .filter(l => LeadHelper.isQualified(l) && l?.campaigns.includes(campaign.id))
            .filter(util.existsFilter));

        qualifiedLeads = util.distinctId<Lead>(store.getState().leads
            .getMappingFor('campaignId', campaign.id)
            // .filter(l => l?.campaigns.includes(campaign.id))
            .filter(util.existsFilter)
            .filter(LeadHelper.isQualified));
    }
    else {
        convertedLeads = store.getState().matters
            .map(m => LeadHelper.getLeadFromId(m.leadId))
            .filter(util.existsFilter)
            .filter(LeadHelper.isQualified);

        qualifiedLeads = store.getState().leads
            .filter(LeadHelper.isQualified);
    }

    if (qualifiedLeads.length === 0) return 0;
    else return util.fixed(100 * convertedLeads.length / qualifiedLeads.length, 0);
};

/**
 * Get the ROI calculations for campaigns, given the date constraints.
 *
 * Date ranges are interpreted as follows:
 * - Spend will be based on money spent between the date ranges given. This is taken from campaigns.
 * - Returns will be based on money *collected* during the given period. This is taken from invoices.
 *
 * @param {Campaign[]} campaigns
 * @param {Date} [startDate]
 * @param {Date} [endDate]
 */
export const getRoiChartData = (campaigns: Campaign[] | ArrayMap<Campaign>, startDate?: Date, endDate?: Date): RoiChartDataResult[] => {
    const colors = [...ChartProps.generateColors(.8, .5), ...ChartProps.generateColors(.6, .4)];

    return campaigns.map((c: Campaign, i: number) => {
        const roiBC = getCampaignBilledAndCollected(c, startDate, endDate);
        const totalCost = getTotalCost(c, null, false, startDate, endDate) as number;
        let roi = (roiBC.collected || 0) / (totalCost || 1);
        if (roi > 20) roi = 20;
        return {
            id: c.name,
            data: [
                {x: 'Spend', y: 1 + roi / 100}, // add a fraction of the roi so that we can sort these from best to worst ROI
                {x: 'Returns', y: roi} //
                // {x: 'Spend', y: getTotalCost(c, tasks, false, startDate, endDate)},
                // {x: 'Returns', y: roi?.collected || 0}
            ],
            spent: totalCost,
            received: roiBC.collected,
            roi: roiBC,
            color: colors[i]
        };
    })
        .filter(o => o.spent > 0 || o.received > 0);
}

export type RoiChartDataResult = {
    id: string;
    data: {
        x: string;
        y: number;
    }[];
    spent: number;
    received: number;
    roi: {
        billed: number;
        collected: number;
    };
    color: string;
}